import { CountryCode, MARKETPLACE_IMAGE_OPTIONS, RefundStatus, RefundType } from '@/Enums';
import { AddressModel, OrderLabelModel } from '@/Models';
import { OrderAutomatizaionModel } from '@/Models/OrderUserProducts/OrderAutomatizaionModel';
import { OrderLabelPrintRow, OrderTableRow, OrderAutomatizaionRow, OrderTableRowItem } from '@/Types';
import { stringUtils } from '@/Utils';
import { OrderItemModel } from '@/Models/Order/OrderItemModel';
import { OrderModel } from '@/Models/Order/OrderModel';

function convertOrderToOrderTableRow(order: OrderModel): OrderTableRow {
  return {
    id: order.id,
    userOrderId: order.userOrderId,
    userProductId: order.userProductId,
    marketplaceType: order.marketplaceType,
    parentOrder: order.parentOrder,
    children: order.children.map((item) => JSON.parse(JSON.stringify(item))) ?? [],
    orderStatus: order.orderStatus,
    deliveryStatus: order.deliveryStatus,
    cancelStatus: order.cancelStatus,
    orderType: order.orderType,
    paymentsProgramType: order.paymentsProgramType,
    mpOrderNumber: order.mpOrderNumber ?? '',
    extendedOrderId: order.extendedOrderId ?? '',
    buyerMarketplaceAccount: order.buyerMarketplaceAccount ?? '',
    sellerMarketplaceAccount: order.sellerMarketplaceAccount ?? '',
    accountName: order.accountName ?? '',
    buyerEmail: order.buyerEmail ?? '',
    buyerVatNumber: order.buyerVatNumber ?? '',
    termsOfPayment: order.termsOfPayment,
    paymentDueDate: order.paymentDueDate ?? '',
    paymentDueDays: order.paymentDueDays ?? '',
    invoiceType: order.invoiceType,
    mpAccountId: order.mpAccountId ?? '',
    marketplace: order.marketplace ?? '',
    history: order.history,
    quantity: order.orderItems.reduce((acc, item) => acc + item.quantity, 0),

    refundTime: order.refundTime ?? '',
    refundAmount: order.refundAmount ?? '',
    refundType: order.refundType ?? ('' as RefundType),
    refundStatus: order.refundStatus ?? ('' as RefundStatus),
    shippingStatuses: order.shippingStatuses,

    orderBalance: order.orderBalance ?? '',
    priceBruttoWithShipping: order.priceBruttoWithShipping ?? '',
    priceBruttoWithoutShipping: order.priceBruttoWithoutShipping ?? '',
    totalNettoWithShipping: order.totalNettoWithShipping ?? '',
    amountPaid: order.amountPaid ?? '',
    shippingPrice: +order.shippingPrice.toFixed(2) ?? '',
    openAmount: order.openAmount ?? '',
    orderFixFee: order.orderFixFee ?? '',
    marketplaceFee: order.marketplaceFee ?? '',
    marketplacePayout: order.marketplacePayout ?? '',
    currencyId: order.currencyId ?? '',
    category: order.category ?? '',

    commonVat1Percent: order.commonVat1Percent ?? '',
    commonVat1Amount: order.commonVat1Amount ?? '',
    commonNettoVat1Amount: order.commonNettoVat1Amount ?? '',
    commonVat2Percent: order.commonVat2Percent,
    commonVat2Amount: order.commonVat2Amount,
    commonNettoVat2Amount: order.commonNettoVat2Amount ?? '',
    commonVat3Percent: order.commonVat3Percent ?? '',
    commonVat3Amount: order.commonVat3Amount ?? '',
    commonNettoVat3Amount: order.commonNettoVat3Amount ?? '',
    commonVat4Percent: order.commonVat4Percent ?? '',
    commonVat4Amount: order.commonVat4Amount ?? '',
    commonNettoVat4Amount: order.commonNettoVat4Amount ?? '',
    commonVat5Percent: order.commonVat5Percent ?? '',
    commonVat5Amount: order.commonVat5Amount ?? '',
    commonNettoVat5Amount: order.commonNettoVat5Amount ?? '',
    countryVatId: order.countryVatId ?? '',
    vatCountry: order.vatCountry ?? ('' as CountryCode),

    paymentStatus: order.paymentStatus ?? '',
    paymentMethod: order.paymentMethod ?? '',
    appCreatedTime: order.appCreatedTime ?? '',
    appLastModifiedTime: order.appLastModifiedTime ?? '',
    synchTime: order.synchTime ?? '',
    appDeleted: order.appDeleted ?? '',
    mpLastModifiedTime: order.mpLastModifiedTime ?? '',
    mpCreatedTime: order.mpCreatedTime ?? '',
    paidTime: order.paidTime ?? '',
    isDeliveryRequired: order.isDeliveryRequired ?? false,
    shippingMethodCode: order.shippingMethodCode ?? '',
    shippingMethodDescription: order.shippingMethodDescription ?? '',
    shippedTime: order.shippedTime ?? '',
    estimatedDeliveryTimeMax: order.estimatedDeliveryTimeMax ?? '',
    estimatedDeliveryTimeMin: order.estimatedDeliveryTimeMin ?? '',
    handleByTime: order.handleByTime ?? '',
    actualDeliveryTime: order.actualDeliveryTime ?? '',
    shipFromCountry: order.shipFromCountry ?? ('' as CountryCode),
    exportOutsideEu: order.exportOutsideEu ?? false,
    invoiceConfirmedOnMarketplace: order.invoiceConfirmedOnMarketplace ?? '',
    warehouseLocationName: order.warehouseLocationName ?? '',
    warehouseLocationId: order.warehouseLocationId ?? '',

    shippingCompanyName: order.shippingCompanyName ?? '',
    shippingFirstName: order.shippingFirstName ?? '',
    shippingLastName: order.shippingLastName ?? '',
    shippingAdditionalLine: order.shippingAdditionalLine ?? '',
    shippingStreet: order.shippingStreet ?? '',
    shippingHouseNo: order.shippingHouseNo ?? '',
    shippingPostalCode: order.shippingPostalCode ?? '',
    shippingCityName: order.shippingCityName ?? '',
    shippingCountryCode: order.shippingCountryCode,
    shippingCountryName: order.shippingCountryName ?? '',
    shippingPhoneNumber: order.shippingPhoneNumber ?? '',

    originalShippingCompanyName: order.originalShippingCompanyName ?? '',
    originalShippingFirstName: order.originalShippingFirstName ?? '',
    originalShippingLastName: order.originalShippingLastName ?? '',
    originalShippingAdditionalLine: order.originalShippingAdditionalLine ?? '',
    originalShippingStreet: order.originalShippingStreet ?? '',
    originalShippingHouseNo: order.originalShippingHouseNo ?? '',
    originalShippingPostalCode: order.originalShippingPostalCode ?? '',
    originalShippingCityName: order.originalShippingCityName ?? '',
    originalShippingCountryCode: order.originalShippingCountryCode ?? '',
    originalShippingCountryName: order.originalShippingCountryName ?? '',
    originalShippingPhoneNumber: order.originalShippingPhoneNumber ?? '',

    invoiceCompanyName: order.invoiceCompanyName ?? '',
    invoiceFirstName: order.invoiceFirstName ?? '',
    invoiceLastName: order.invoiceLastName ?? '',
    invoiceAdditionalLine: order.invoiceAdditionalLine ?? '',
    invoiceStreet: order.invoiceStreet ?? '',
    invoiceHouseNo: order.invoiceHouseNo ?? '',
    invoicePostalCode: order.invoicePostalCode ?? '',
    invoiceCityName: order.invoiceCityName ?? '',
    invoiceCountryCode: order.invoiceCountryCode ?? '',
    invoiceCountryName: order.invoiceCountryName ?? '',
    invoicePhoneNumber: order.invoicePhoneNumber ?? '',

    returnCompanyName: order.returnCompanyName ?? '',
    returnFirstName: order.returnFirstName ?? '',
    returnLastName: order.returnLastName ?? '',
    returnAdditionalLine: order.returnAdditionalLine ?? '',
    returnStreet: order.returnStreet ?? '',
    returnHouseNo: order.returnHouseNo ?? '',
    returnPostalCode: order.returnPostalCode ?? '',
    returnCityName: order.returnCityName ?? '',
    returnCountryCode: order.returnCountryCode ?? '',
    returnCountryName: order.invoiceCountryName ?? '',
    returnPhoneNumber: order.returnPhoneNumber ?? '',

    packageName1: order.packageName1 ?? '',
    packageName2: order.packageName2 ?? '',
    packageName3: order.packageName3 ?? '',
    packageName4: order.packageName4 ?? '',
    packageName5: order.packageName5 ?? '',

    //#region Application generation fields
    customerNumber: order.customerNumber ?? '',
    customerNumberCreatedTime: order.customerNumberCreatedTime ?? '',

    deliveryNoteNumber: order.deliveryNoteNumber ?? '',
    deliveryNoteNumberCreatedTime: order.deliveryNoteNumberCreatedTime ?? '',
    deliveryNotePrinted: order.deliveryNotePrinted ?? '',
    deliveryNoteMailed: order.deliveryNoteMailed ?? '',

    orderNumber: order.orderNumber ?? '',
    orderNumberCreatedTime: order.orderNumberCreatedTime ?? '',
    orderPrinted: order.orderPrinted ?? '',
    orderMailed: order.orderMailed ?? '',

    invoices: order.invoices,
    currentInvoice: order.currentInvoice,
    currentCancelInvoice: order.currentCancelInvoice,

    cancelInvoiceNumber: order.cancelInvoiceNumber ?? '',
    invoiceNumber: order.invoiceNumber ?? '',

    internalNote: order.internalNote ?? '',
    externalNote: order.externalNote ?? '',

    actualShippingMethodCode1: order.actualShippingMethodCode1 ?? '',
    actualShippingMethodCode2: order.actualShippingMethodCode2 ?? '',
    actualShippingMethodCode3: order.actualShippingMethodCode3 ?? '',
    actualShippingMethodCode4: order.actualShippingMethodCode4 ?? '',
    actualShippingMethodCode5: order.actualShippingMethodCode5 ?? '',
    actualShippingMethodDescription: order.actualShippingMethodDescription ?? '',
    actualShippingCostNetto: order.actualShippingCostNetto ?? '',

    shippingType1: order.shippingType1 ?? '',
    shippingType2: order.shippingType2 ?? '',
    shippingType3: order.shippingType3 ?? '',
    shippingType4: order.shippingType4 ?? '',
    shippingType5: order.shippingType5 ?? '',

    profit: order.profit ?? '',

    isBlocked: order.isBlocked ?? '',
    archived: order.archived ?? '',
    exported: order.exported ?? '',
    canceledNotPaid: order.canceledNotPaid ?? '',
    canceledRefunded: order.canceledRefunded ?? '',
    notifiedTime: order.notifiedTime ?? '',

    addressEditDisabled: order.addressEditDisabled ?? '',
    orderEditDisabled: order.orderEditDisabled ?? '',
    //#endregion Application generation fields

    buyerNote: order.buyerNote ?? '',
    sellerNote: order.sellerNote ?? '',

    isOriginalShippingModified: order.isOriginalShippingModified ?? false,

    secondContactPersonEmail: order.secondContactPersonEmail ?? '',
    secondContactPersonName: order.secondContactPersonName ?? '',
    secondContactPersonNotes: order.secondContactPersonNotes ?? '',
    secondContactPersonPhones: order.secondContactPersonPhones ?? '',
    secondContactPersonTitle: order.secondContactPersonTitle ?? '',

    isAmazonPrime: order.isAmazonPrime ?? false,
    isPremiumOrder: order.isPremiumOrder ?? false,
    isBusinessOrder: order.isBusinessOrder ?? false,
    shipmentServiceLevelCategory: order.shipmentServiceLevelCategory ?? '',
    fulfillmentChannel: order.fulfillmentChannel ?? '',
    mock: order.mock ?? '',
    warnings: order.warnings,
    mandatName: order.mandatName ?? '',
    mandatId: order.mandatId ?? '',
    useShippingAutomation: order.useShippingAutomation ?? false,

    taxAddressRole: order.taxAddressRole ?? '',
    taxReportingScheme: order.taxReportingScheme ?? '',
    jurisdictionName: order.jurisdictionName ?? '',

    exchangeRate: order.exchangeRate ?? '',
    exchangeRateDate: order.exchangeRateDate ?? '',
    exchangeRateCurrencyTo: order.exchangeRateCurrencyTo ?? '',

    orderItems: order.orderItems.map(convertOrderItem),
    currentInvoiceNumber: order.currentInvoice?.number ?? '',
    currentInvoiceCreatedTime: order.currentInvoice?.createdTime ?? '',
    currentCancelInvoiceNumber: order.currentCancelInvoice?.number ?? '',
    currentCancelInvoiceCreatedTime: order.currentCancelInvoice?.createdTime ?? '',
  };
}

function convertOrderItem(orderItem: OrderItemModel): OrderTableRowItem {
  return {
    id: orderItem.id,
    marketplaceId: orderItem.marketplaceId ?? '',
    transactionId: orderItem.transactionId ?? '',
    ebayPlus: orderItem.ebayPlus ?? false,
    sku: orderItem.sku ?? '',
    warehousePlace: orderItem.warehousePlace ?? '',
    title: orderItem.title ?? '',
    variantTitle: orderItem.variantTitle ?? '',
    shippingCostBrutto: orderItem.shippingCostBrutto ?? '',
    shippingCostNetto: orderItem.shippingCostNetto ?? '',

    priceNetto: +orderItem.priceNetto.toFixed(2) ?? '',
    priceBrutto: +orderItem.priceBrutto.toFixed(2) ?? '',
    totalPriceNettoWithoutShipping: +orderItem.totalPriceNettoWithoutShipping.toFixed(2) ?? '',
    totalPriceBruttoWithoutShipping: +orderItem.totalPriceBruttoWithoutShipping.toFixed(2) ?? '',
    salesTaxTotalWithShipping: +orderItem.salesTaxTotalWithShipping.toFixed(2) ?? '',
    totalPriceNettoWithShipping: +orderItem.totalPriceNettoWithShipping.toFixed(2) ?? '',
    totalPriceBruttoWithShipping: +orderItem.totalPriceBruttoWithShipping.toFixed(2) ?? '',
    transactionVariableFee: +orderItem.transactionVariableFee.toFixed(2) ?? '',
    salesTaxPercent: orderItem.salesTaxPercent ?? '',
    promotionFee: orderItem.promotionFee ?? '',
    saleFormat: orderItem.saleFormat ?? '',

    quantity: orderItem.quantity ?? '',
    shippingQuantity: orderItem.shippingQuantity ?? '',
    trackingNumber: orderItem.trackingNumber ?? '',
    sellerFeedback: orderItem.sellerFeedback,
    buyerFeedback: orderItem.buyerFeedback,
    category: orderItem.category ?? '',

    variantName1: orderItem.variantName1 ?? '',
    variantValue1: orderItem.variantValue1 ?? '',

    variantName2: orderItem.variantName2 ?? '',
    variantValue2: orderItem.variantValue2 ?? '',

    variantName3: orderItem.variantName3 ?? '',
    variantValue3: orderItem.variantValue3 ?? '',

    variantName4: orderItem.variantName4 ?? '',
    variantValue4: orderItem.variantValue4 ?? '',

    variantName5: orderItem.variantName5 ?? '',
    variantValue5: orderItem.variantValue5 ?? '',

    purchaseNet: orderItem.purchaseNet ?? '',

    marketplacePayout: orderItem.marketplacePayout ?? '',

    // ...orderItem,
    orderItemId: orderItem.id,
    variants: getVariants(orderItem),
    shippingCostBruttoPerPosition: +orderItem.shippingCostBrutto.toFixed(2) ?? '',
    shippingCostNettoPerPosition: +orderItem.shippingCostNetto.toFixed(2) ?? '',

    ...(orderItem.buyerFeedback && {
      buyerFeedbackText: orderItem.buyerFeedback.text,
      buyerFeedbackTime: orderItem.buyerFeedback.commentTime,
      buyerFeedbackType: orderItem.buyerFeedback.ratingType,
    }),

    ...(orderItem.sellerFeedback && {
      sellerFeedbackText: orderItem.sellerFeedback.text,
      sellerFeedbackTime: orderItem.sellerFeedback.commentTime,
      sellerFeedbackType: orderItem.sellerFeedback.ratingType,
    }),
  };
}

function convertOrdersToOrderTableRows(orders: OrderModel[]): OrderTableRow[] {
  return orders.map(convertOrderToOrderTableRow);
}

function getVariants(orderItem: OrderItemModel): string {
  return [
    orderItem.variantValue1,
    orderItem.variantValue2,
    orderItem.variantValue3,
    orderItem.variantValue4,
    orderItem.variantValue5,
  ]
    .filter((x) => x)
    .join(', ');
}

function getShippingInfo(order: OrderModel): AddressModel {
  return {
    additionalLine: order.shippingAdditionalLine,
    city: order.shippingCityName,
    company: order.shippingCompanyName,
    countryCode: order.shippingCountryCode,
    country: order.shippingCountryName,
    firstName: order.shippingFirstName,
    houseNo: order.shippingHouseNo,
    lastName: order.shippingLastName,
    phone: order.shippingPhoneNumber,
    postalCode: order.shippingPostalCode,
    street: order.shippingStreet,
    email: order.buyerEmail,
  };
}

function updateShippingAddress(order: OrderTableRow, address: AddressModel): OrderTableRow {
  return {
    ...order,
    shippingCityName: address.city,
    shippingCompanyName: address.company,
    shippingAdditionalLine: address.additionalLine,
    shippingCountryCode: address.countryCode,
    shippingCountryName: address.country,
    shippingHouseNo: address.houseNo,
    shippingStreet: address.street,
    shippingFirstName: address.firstName,
    shippingLastName: address.lastName,
    shippingPhoneNumber: address.phone,
    buyerEmail: address.email,
    shippingPostalCode: address.postalCode,
    isOriginalShippingModified: true,
  };
}

function copyShippingInfo(destination: OrderTableRow, source: OrderModel): OrderTableRow {
  const {
    shippingCompanyName,
    shippingFirstName,
    shippingLastName,
    shippingAdditionalLine,
    shippingStreet,
    shippingHouseNo,
    shippingPostalCode,
    shippingCityName,
    shippingCountryCode,
    shippingCountryName,
    shippingPhoneNumber,
    isOriginalShippingModified,
  } = source;
  return {
    ...destination,
    shippingCompanyName,
    shippingFirstName,
    shippingLastName,
    shippingAdditionalLine,
    shippingStreet,
    shippingHouseNo,
    shippingPostalCode,
    shippingCityName,
    shippingCountryCode,
    shippingCountryName,
    shippingPhoneNumber,
    isOriginalShippingModified,
  };
}

function getOrderAutomatizaionRow(order: OrderAutomatizaionModel): OrderAutomatizaionRow {
  return {
    ...order,
    marketplaceImage: stringUtils.enumToString(order.marketplaceType, MARKETPLACE_IMAGE_OPTIONS),
  };
}

function convertToOrderPrintRows(orderLabels: OrderLabelModel[]): OrderLabelPrintRow[] {
  return orderLabels?.map((orderLabel) => {
    return {
      marketplaceType: orderLabel.marketplaceType,
      marketplaceImage: stringUtils.enumToString(orderLabel.marketplaceType, MARKETPLACE_IMAGE_OPTIONS),
      id: orderLabel.id,
      orderId: orderLabel.orderId,
      userOrderId: orderLabel.userOrderId,
      invoice: orderLabel.invoice,
      userLogisticTypeId: orderLabel.userLogisticTypeId,
      userLogisticTypeName: orderLabel.userLogisticTypeName,
      country: orderLabel.country,
      countryName: orderLabel.countryName,
      packages: orderLabel.packages,
      logisticDesciptions: `${orderLabel.packages.length}x ${orderLabel.userLogisticTypeName}`,
      price: orderLabel.price,
      isCreated: orderLabel.isCreated,
      isPrinted: orderLabel.isPrinted,
      isPickup: orderLabel.isPickup,
    };
  });
}

export const orderMapping = {
  convertOrdersToOrderTableRows,
  convertOrderToOrderTableRow,
  updateShippingAddress,
  getShippingInfo,
  copyShippingInfo,
  getOrderAutomatizaionRow,
  convertToOrderPrintRows,
  getVariants,
};
